import { Grid, Typography, Button, Collapse, DialogContent } from '@mui/material';
import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { CheckCircleOutlineRounded, CheckCircleRounded } from '@mui/icons-material';
import { TimelineRounded, DescriptionRounded } from '@mui/icons-material';
import { CircularProgress, FormControlLabel, Switch } from '@mui/material';
import StateManager from '../../../../Global/StateManager';
import { useHistory } from 'react-router-dom';
import { ProcessIcon, FormIcon } from '../../../../Global/Icons';
import { User, DatePicker, TimePicker } from '../../../../Global/Components';
import { RoundedDialog, StandardDialogActions } from '../../../../Global/Components';
import { grey, green } from '@mui/material/colors';
import { isEmpty, isArray } from 'lodash';
import axios from 'axios';
import moment from 'moment';

const ReferenceItem = styled(Grid)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(1.5),
}));

export function ActionButton({ action, parentFormEntryId }) {
  const [starting, setStarting] = useState(false);
  const [optionsDialog, setOptionsDialog] = useState(false);
  const history = useHistory();

  function checkProcessUsers() {
    const selectable =
      (action.userType === 'selectedUsers' && action.provideUserChoice && !isEmpty(action.users)) ||
      action.startAnytime;

    if (selectable) {
      setOptionsDialog(true);
    } else {
      startProcess();
    }
  }

  function checkFormUsers() {
    const selectable =
      (action.userType === 'selectedUsers' && action.provideUserChoice && !isEmpty(action.users)) ||
      action.startAnytime;

    if (selectable) {
      setOptionsDialog(true);
    } else {
      startForm();
    }
  }

  function startProcess(params) {
    if (!action?.processId) return;
    setStarting(true);
    const dueAt = params
      ? params.dueAt
      : action.due
      ? moment().add(action.due.hours, 'hours').add(action.due.days, 'days').toDate()
      : null;

    const body = {
      processId: action.processId,
      parentFormEntryId,
      assignedUsers: params
        ? params.selectedUsers
        : action.userType === 'selectedUsers' && !isEmpty(action.users)
        ? action.users
        : null,
      dueAt,
      formActionId: action.id,
    };

    axios
      .post('/process/startProcess', body)
      .then((res) => {
        StateManager.setSuccessAlert('Process has been started');
        setStarting(false);
        history.push(`/processes/ongoing/${res.data.ongoingProcess._id}`);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setStarting(false);
      });
  }

  function startForm(params) {
    if (!action?.formId) return;
    setStarting(true);

    const dueAt = params
      ? params.dueAt
      : action.due
      ? moment().add(action.due.hours, 'hours').add(action.due.days, 'days').toDate()
      : null;

    const body = {
      formId: action.formId,
      parentFormEntryId,
      assignedUsers: params
        ? params.selectedUsers
        : action.userType === 'selectedUsers' && !isEmpty(action.users)
        ? action.users
        : null,
      dueAt,
      formActionId: action.id,
    };

    axios
      .post('/forms/entries/sendEntry', body)
      .then((res) => {
        StateManager.setSuccessAlert('Process has been started');
        setStarting(false);
        history.push(`/forms/entry/${res.data[0]._id}`);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setStarting(false);
      });
  }

  if (!action) return null;

  if (action.actionType === 'startProcess') {
    return (
      <ReferenceItem container justifyContent="center" alignItems="center">
        <Button
          variant="contained"
          color="primary"
          style={{ textTransform: 'none', borderRadius: 8 }}
          startIcon={<TimelineRounded />}
          endIcon={starting ? <CircularProgress size={24} style={{ color: 'white' }} /> : null}
          onClick={checkProcessUsers}
        >
          Start process: {action.processTitle}
        </Button>
        <OptionsDialog
          action={action}
          open={optionsDialog}
          onClose={() => setOptionsDialog(false)}
          onResult={startProcess}
        />
      </ReferenceItem>
    );
  }

  if (action.actionType === 'startForm') {
    return (
      <ReferenceItem container justifyContent="center" alignItems="center">
        <Button
          variant="contained"
          color="primary"
          style={{ textTransform: 'none', borderRadius: 8 }}
          startIcon={<DescriptionRounded />}
          endIcon={starting ? <CircularProgress size={24} style={{ color: 'white' }} /> : null}
          onClick={checkFormUsers}
        >
          Start form: {action.formTitle}
        </Button>
        <OptionsDialog
          action={action}
          open={optionsDialog}
          onClose={() => setOptionsDialog(false)}
          onResult={startForm}
        />
      </ReferenceItem>
    );
  }

  return null;
}
function OptionsDialog({ action, open, onClose, onResult }) {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [includeDueDate, setIncludeDueDate] = useState(false);
  const [dueAt, setDueAt] = useState(null);

  const defaultDue = action.due
    ? moment().add(action.due.hours, 'hours').add(action.due.days, 'days').toDate()
    : moment().add(7, 'days').set({ hours: 17, minutes: 0, seconds: 0, milliseconds: 0 });

  useEffect(() => {
    if (action?.due) {
      setIncludeDueDate(true);
      setDueAt(defaultDue);
    }
  }, [action]); // eslint-disable-line

  const choseUsers =
    action.userType === 'selectedUsers' && !isEmpty(action.users) && isArray(action.users) && action.provideUserChoice;
  const choseDue = action.startAnytime;

  const hideDone = choseUsers && isEmpty(selectedUsers);

  function done() {
    const result = { selectedUsers, dueAt: includeDueDate ? (dueAt ? moment(dueAt).toDate() : null) : null };
    onResult(result);
    onClose();
  }

  if (!action) return null;

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent>
        <Grid container item style={{ margin: '1rem 0' }} wrap="nowrap" alignItems="center">
          {action.actionType === 'startProcess' && <ProcessIcon />}
          {action.actionType === 'startForm' && <FormIcon />}
          {action.actionType === 'sendTask' && <TaskIcon />}
          <Typography style={{ marginLeft: 8, whiteSpace: 'break-spaces' }}>
            You are about to{' '}
            {action.actionType === 'startProcess' && (
              <>
                start process: {'\n'}
                <span style={{ fontWeight: 500 }}>{action.processTitle}</span>
              </>
            )}
            {action.actionType === 'startForm' && (
              <>
                start form entry: {'\n'}
                <span style={{ fontWeight: 500 }}>{action.formTitle}</span>
              </>
            )}
            {action.actionType === 'sendTask' && (
              <>
                send task: {'\n'}
                <span style={{ fontWeight: 500 }}>{action.task?.title}</span>
              </>
            )}
          </Typography>
        </Grid>
        {choseUsers && (
          <Grid container item style={{ margin: '1rem 0' }}>
            <Grid container item>
              <Typography variant="h6" gutterBottom>
                Pick users:
              </Typography>
            </Grid>

            <Grid container item>
              {action.users.map((user, i) => (
                <User
                  key={i}
                  id={user}
                  fullWidth
                  markPortal
                  onClick={() => {
                    if (selectedUsers.includes(user)) {
                      setSelectedUsers(selectedUsers.filter((x) => x !== user));
                    } else {
                      setSelectedUsers([...selectedUsers, user]);
                    }
                  }}
                  action={
                    selectedUsers.includes(user) ? (
                      <CheckCircleRounded fontSize="large" style={{ color: green[500] }} />
                    ) : (
                      <CheckCircleOutlineRounded fontSize="large" style={{ color: grey[300] }} />
                    )
                  }
                />
              ))}
            </Grid>
          </Grid>
        )}

        {choseDue && (
          <Grid container>
            <Grid container>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={includeDueDate}
                    onChange={(e) => {
                      setIncludeDueDate(e.target.checked);
                      if (e.target.checked && !dueAt) {
                        setDueAt(defaultDue);
                      }
                    }}
                  />
                }
                label="Include due date"
              />
            </Grid>
            <Collapse unmountOnExit style={{ width: '100%' }} in={includeDueDate}>
              <Grid container>
                <Grid item container xs={6} style={{ paddingRight: 4 }}>
                  <DatePicker value={dueAt} onChange={setDueAt} />
                </Grid>

                <Grid item container xs={6} style={{ paddingLeft: 4 }}>
                  <TimePicker value={dueAt} onChange={setDueAt} />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        )}
      </DialogContent>
      <StandardDialogActions onClose={onClose} onDone={done} hideDone={hideDone} />
    </RoundedDialog>
  );
}
